import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { Ticket as TicketIcon, Search, Filter, RefreshCw, Eye, Clock, User, AlertCircle, CheckCircle, XCircle, Pause, Plus } from 'lucide-react';

interface Ticket {
  id: string;
  ticket_number: number;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_date: string;
  last_modified_date: string;
  contact_id: string | null;
  service_item_id?: string | null;
  location_id?: string | null;
  issue_type_id?: string | null;
  project_id?: string | null;
  time_rounded?: number;
  is_billable?: boolean;
  is_taxable?: boolean;
  completed_date?: string | null;
  due_date?: string | null;
  created_by?: string;
  updated_by?: string;
  ticket_custom_status_id?: string;
  conversation?: ConversationItem[];
}

interface ConversationItem {
  id: string;
  content: string;
  created_date: string;
  updated_date?: string; // Added updated_date
  created_by: string;
  from_address: string;
  response_type_id: number;
  user_id?: string; // Added user_id
  type: string;
  is_public: boolean;
  is_internal: boolean;
}

interface TicketResponse {
  tickets: Ticket[];
  total_count: number;
  is_admin_view: boolean;
}

const Tickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // Default to active tickets
  const [sortBy, setSortBy] = useState<string>('newest'); // Default to newest first
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loadingTicketDetails, setLoadingTicketDetails] = useState<boolean>(false);
  const [isAdminView, setIsAdminView] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [newResponse, setNewResponse] = useState<string>('');
  const [submittingResponse, setSubmittingResponse] = useState<boolean>(false);

  // Create ticket modal state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newTicketSubject, setNewTicketSubject] = useState<string>('');
  const [newTicketDescription, setNewTicketDescription] = useState<string>('');
  const [newTicketPriority, setNewTicketPriority] = useState<string>('medium');
  const [creatingTicket, setCreatingTicket] = useState<boolean>(false);

  const currentUser = authService.getCurrentUser();
  const isAdmin = authService.isAdmin();

  // Debounced search effect - wait 500ms after user stops typing
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      loadTickets();
    }, searchTerm ? 500 : 0); // Immediate load if no search term

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, statusFilter, sortBy]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: { search?: string; status?: string; sort?: string; limit?: number } = {
        limit: 100
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (sortBy) {
        params.sort = sortBy;
      }
      
      const response = await apiService.getTickets(params);
      
      if (response.error || response.errors) {
        setError(response.message || 'Failed to load tickets');
        setTickets([]);
      } else {
        // The API response has data nested under 'result'
        const data: TicketResponse = response.result || response.data;
        setTickets(data.tickets || []);
        setTotalCount(data.total_count || 0);
        setIsAdminView(data.is_admin_view || false);
      }
    } catch (err: any) {
      console.error('Error loading tickets:', err);
      setError(err.response?.data?.message || 'Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = async (ticket: Ticket) => {
    try {
      setLoadingTicketDetails(true);
      setError(''); // Clear any previous errors
      
      const response = await apiService.getTicket(ticket.id);
      if (!response.error && !response.errors) {
        // Handle nested response structure
        const ticketData = response.result || response.data;
        setSelectedTicket(ticketData);
        setNewResponse(''); // Clear response form
      } else {
        setError(response.message || 'Failed to load ticket details');
      }
    } catch (err: any) {
      console.error('Error loading ticket details:', err);
      setError('Failed to load ticket details');
    } finally {
      setLoadingTicketDetails(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedTicket || !newResponse.trim()) return;
    
    try {
      setSubmittingResponse(true);
      setError('');
      
      const response = await apiService.addTicketResponse(selectedTicket.id, newResponse.trim());
      
      if (!response.error && !response.errors) {
        // Success - refresh the ticket details to show the new response
        setNewResponse('');
        await handleTicketClick(selectedTicket); // Reload ticket data
      } else {
        setError(response.message || 'Failed to create response');
      }
    } catch (err: any) {
      console.error('Error creating response:', err);
      setError('Failed to create response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketSubject.trim() || !newTicketDescription.trim()) return;

    try {
      setCreatingTicket(true);
      setError('');

      const response = await apiService.createTicket({
        subject: newTicketSubject.trim(),
        description: newTicketDescription.trim(),
        priority: newTicketPriority,
      });

      if (!response.error && !response.errors) {
        // Success - close modal and refresh tickets
        setShowCreateModal(false);
        setNewTicketSubject('');
        setNewTicketDescription('');
        setNewTicketPriority('medium');
        await loadTickets();
      } else {
        setError(response.message || 'Failed to create ticket');
      }
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'in progress':
      case 'in-progress':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
      case 'resolved':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return <AlertCircle className="w-3 h-3 mr-1" />;
      case 'in progress':
      case 'in-progress':
        return <Pause className="w-3 h-3 mr-1" />;
      case 'closed':
        return <XCircle className="w-3 h-3 mr-1" />;
      case 'resolved':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      default:
        return <AlertCircle className="w-3 h-3 mr-1" />;
    }
  };

  const getPriorityBadgeClass = (priority: string): string => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      case 'medium':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800';
      case 'low':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    try {
      const userTimezone = currentUser?.time_zone || 'America/New_York'; // Default to Eastern if not set
      
      return new Date(dateString).toLocaleString('en-US', {
        timeZone: userTimezone,
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Tickets</h3>
              <p className="text-gray-500">Please wait while we fetch your support tickets...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <TicketIcon className="w-8 h-8 mr-3 text-blue-600" />
              Support Tickets
            </h1>
            <p className="mt-2 text-gray-600">
              {isAdminView ? 'All tickets for your organization' : 'Your assigned tickets'}
              {totalCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {totalCount} total
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </button>
            {isAdmin && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                <User className="w-4 h-4 mr-1" />
                Admin View
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by ticket title, description, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  searchTerm 
                    ? 'border-blue-300 bg-blue-50 text-blue-900 font-medium' 
                    : 'border-gray-300 bg-white'
                }`}
              />
              {searchTerm && (
                <div className="absolute top-full left-0 right-0 mt-1 text-xs text-gray-500 bg-white border border-gray-200 rounded-md p-2 shadow-sm z-10">
                  <strong>Searching in:</strong> Ticket titles and descriptions | <strong>Tip:</strong> Search is case-insensitive and finds partial matches
                </div>
              )}
            </div>
          </div>
          
          {/* Filters and Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  statusFilter === 'active' 
                    ? 'border-gray-300 bg-white' 
                    : 'border-blue-300 bg-blue-50 text-blue-900 font-medium'
                }`}
              >
                <option value="active">Active (New, Assigned, Accepted, In Progress)</option>
                <option value="">All Tickets</option>
                <option value="New">New</option>
                <option value="Assigned">Assigned</option>
                <option value="Accepted">Accepted</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="closed">Closed (Complete, Reviewed, Billed, Cancelled)</option>
                <option value="Complete">Complete</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex-1 sm:max-w-xs">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8 ${
                  sortBy === 'newest'
                    ? 'border-gray-300 bg-white'
                    : 'border-blue-300 bg-blue-50 text-blue-900 font-medium'
                }`}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="updated">Recently Updated</option>
                <option value="priority">By Priority</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={loadTickets}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
              {loading ? (
                <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              
              {/* Clear Filters Button */}
              {(statusFilter !== 'active' || sortBy !== 'newest' || searchTerm) && (
                <button
                onClick={() => {
                  setStatusFilter('active');
                  setSortBy('newest');
                  setSearchTerm('');
                }}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <XCircle className="w-4 h-4 mr-2" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(statusFilter !== 'active' || sortBy !== 'newest' || searchTerm) && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-gray-500 font-medium">Active filters:</span>
              
              {searchTerm && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {statusFilter !== 'active' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Status: {statusFilter === '' ? 'All Tickets' : statusFilter}
                  <button
                    onClick={() => setStatusFilter('active')}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {sortBy !== 'newest' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Sort: {sortBy === 'oldest' ? 'Oldest First' : sortBy === 'updated' ? 'Recently Updated' : 'By Priority'}
                  <button
                    onClick={() => setSortBy('newest')}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Tickets Grid */}
      {tickets.length === 0 && !loading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <TicketIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter 
              ? 'Try adjusting your search criteria or filters.'
              : 'No support tickets are currently available.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 divide-y divide-gray-200">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        #{ticket.ticket_number} - {ticket.subject}
                      </h3>
                      <span className={getStatusBadgeClass(ticket.status)}>
                        {getStatusIcon(ticket.status)}
                        {ticket.status}
                      </span>
                      <span className={getPriorityBadgeClass(ticket.priority)}>
                        {ticket.priority}
                      </span>
                    </div>
                    
                    {ticket.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {ticket.description.length > 150 
                          ? `${ticket.description.substring(0, 150)}...`
                          : ticket.description}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Created: {formatDate(ticket.created_date)}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Updated: {formatDate(ticket.last_modified_date)}
                      </div>
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        ID: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded ml-1">{ticket.id}</code>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <button
                      onClick={() => handleTicketClick(ticket)}
                      disabled={loadingTicketDetails}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingTicketDetails ? (
                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      {loadingTicketDetails ? 'Loading...' : 'View Details'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading Overlay for Filter Changes */}
      {loading && tickets.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm mx-4">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Updating Tickets</h3>
              <p className="text-gray-500">Applying your filters...</p>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <TicketIcon className="w-5 h-5 mr-2 text-blue-600" />
                Ticket #{selectedTicket.ticket_number}
              </h2>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Subject */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Subject</h3>
                <p className="text-lg font-semibold text-gray-900">{selectedTicket.subject}</p>
              </div>

              {/* Ticket Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-1">Status</h3>
                  <span className={getStatusBadgeClass(selectedTicket.status)}>
                    {getStatusIcon(selectedTicket.status)}
                    {selectedTicket.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-1">Priority</h3>
                  <span className={getPriorityBadgeClass(selectedTicket.priority)}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-1">Created</h3>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(selectedTicket.created_date)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-1">Last Modified</h3>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(selectedTicket.last_modified_date)}</p>
                </div>
              </div>

            {/* Due Date as separate row if exists */}
            {selectedTicket.due_date && (
              <div className="mb-6">
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="text-xs font-medium text-yellow-700 mb-1">Due Date</h3>
                  <p className="text-sm font-semibold text-yellow-900">{formatDate(selectedTicket.due_date)}</p>
                </div>
              </div>
            )}

              {/* Additional ticket information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {typeof selectedTicket.is_billable !== 'undefined' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Billing Status</h3>
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedTicket.is_billable 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedTicket.is_billable ? 'Billable' : 'Non-Billable'}
                      </span>
                      {typeof selectedTicket.is_taxable !== 'undefined' && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedTicket.is_taxable 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedTicket.is_taxable ? 'Taxable' : 'Tax-Free'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {typeof selectedTicket.time_rounded !== 'undefined' && selectedTicket.time_rounded > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Time Logged</h3>
                    <p className="text-gray-900">{selectedTicket.time_rounded} hours</p>
                  </div>
                )}
              </div>

              {selectedTicket.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                      {selectedTicket.description}
                    </pre>
                  </div>
                </div>
              )}

              {/* Conversation History */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Conversation</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="px-3 py-1 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full">
                      <span className="text-xs font-medium text-gray-600">
                        {selectedTicket.conversation?.length || 0} messages
                      </span>
                    </div>
                    <div className="px-2 py-1 bg-green-100 rounded-full">
                      <span className="text-xs text-green-700 font-medium">Live</span>
                    </div>
                  </div>
                </div>
                
                {selectedTicket.conversation && selectedTicket.conversation.length > 0 ? (
                  <div className="space-y-4">
                    {selectedTicket.conversation.map((item, index) => {
                      const isLatest = index === 0; // First in array = most recent (since we order by desc)
                      
                      // Check if this is a customer portal response by looking for "(Customer Portal)" in created_by
                      const isCustomerPortalResponse = item.created_by?.includes('(Customer Portal)');
                      
                      // Extract actual customer name if it's a portal response
                      const displayName = isCustomerPortalResponse 
                        ? item.created_by.replace(' (Customer Portal)', '').trim()
                        : item.created_by;
                      
                      // Determine if it's from ITP (excluding customer portal responses)
                      const isFromITP = !isCustomerPortalResponse && item.from_address?.includes('@itp360.com');
                      
                      return (
                        <div key={item.id || index} className="relative flex group hover:bg-gray-50/50 rounded-xl transition-all duration-200">
                          {/* Timeline */}
                          <div className="flex flex-col items-center mr-3 pt-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 shadow-sm transition-all duration-200 ${
                              isFromITP 
                                ? 'bg-gradient-to-br from-green-400 to-green-600 border-green-300' 
                                : 'bg-gradient-to-br from-blue-400 to-blue-600 border-blue-300'
                            } ${isLatest ? 'ring-2 ring-blue-400 ring-opacity-30' : 'group-hover:scale-105'}`}>
                              <User className="w-4 h-4 text-white" />
                            </div>
                            {/* Timeline line with gradient */}
                            {selectedTicket.conversation && index < selectedTicket.conversation.length - 1 && (
                              <div className="w-1 flex-1 bg-gradient-to-b from-gray-300 to-gray-200 mt-3 min-h-[60px] rounded-full"></div>
                            )}
                          </div>
                          
                          {/* Message content */}
                          <div className={`flex-1 rounded-xl p-5 shadow-sm border transition-all duration-200 group-hover:shadow-md ${
                            isLatest 
                              ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-blue-100/50' 
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}>
                          
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900">
                                  {displayName || 'Unknown User'}
                                </p>
                                {isLatest && (
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                    Latest
                                  </span>
                                )}
                                {isCustomerPortalResponse && (
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                                    Portal
                                  </span>
                                )}
                              </div>
                              {/* Only show email address for non-customer portal responses */}
                              {item.from_address && !isCustomerPortalResponse && (
                                <p className={`text-xs ${
                                  isFromITP ? 'text-green-600' : 'text-blue-600'
                                }`}>
                                  {item.from_address}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {formatDate(item.created_date)}
                                {item.updated_date && item.updated_date !== item.created_date && (
                                  <span className="ml-1">(edited {formatDate(item.updated_date)})</span>
                                )}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              {item.is_internal && (
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                  </svg>
                                  Internal
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                                isFromITP 
                                  ? 'bg-green-50 text-green-700 border-green-200' 
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}>
                                {isFromITP ? (
                                  <>
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-1a1 1 0 00-1-1H9a1 1 0 00-1 1v1a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                                    </svg>
                                    ITP
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    </svg>
                                    Customer
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                          
                          <div className="mt-4 text-sm text-gray-700">
                            {/* Render HTML content for rich emails, plain text for simple responses */}
                            {item.content.includes('<') ? (
                              <div 
                                className="prose prose-sm max-w-none [&_img]:max-w-full [&_img]:h-auto [&_a]:text-blue-600 [&_a]:underline [&_p]:mb-2 bg-white/50 rounded-lg p-3 border border-gray-100"
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }}
                              />
                            ) : (
                              <div className="bg-white/50 rounded-lg p-3 border border-gray-100">
                                <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed">
                                  {item.content}
                                </pre>
                              </div>
                            )}
                          </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">No conversation history</h4>
                    <p className="text-xs text-gray-500">
                      No comments or activities have been recorded for this ticket yet.
                    </p>
                  </div>
                )}

                {/* Add Response Form - N-able confirmed approach using monitored mailbox */}
                {true && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      Add Response
                    </h4>
                    
                    <div className="space-y-3">
                      <textarea
                        value={newResponse}
                        onChange={(e) => setNewResponse(e.target.value)}
                        placeholder="Type your response here..."
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        disabled={submittingResponse}
                      />
                      
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                          Response will be sent as: {currentUser?.firstname} {currentUser?.lastname} ({currentUser?.email})
                        </p>
                        
                        <button
                          onClick={handleSubmitResponse}
                          disabled={!newResponse.trim() || submittingResponse}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {submittingResponse ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                              </svg>
                              Send Response
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setSelectedTicket(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Plus className="w-5 h-5 mr-2 text-blue-600" />
                Create New Ticket
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTicketSubject('');
                  setNewTicketDescription('');
                  setNewTicketPriority('medium');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Subject */}
              <div>
                <label htmlFor="ticket-subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="ticket-subject"
                  value={newTicketSubject}
                  onChange={(e) => setNewTicketSubject(e.target.value)}
                  placeholder="Brief summary of your issue..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={creatingTicket}
                />
              </div>

              {/* Priority */}
              <div>
                <label htmlFor="ticket-priority" className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  id="ticket-priority"
                  value={newTicketPriority}
                  onChange={(e) => setNewTicketPriority(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={creatingTicket}
                >
                  <option value="low">Low - General question or minor issue</option>
                  <option value="medium">Medium - Issue affecting work but has workaround</option>
                  <option value="high">High - Significant impact, needs attention soon</option>
                  <option value="urgent">Urgent - Critical issue, service down</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="ticket-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="ticket-description"
                  value={newTicketDescription}
                  onChange={(e) => setNewTicketDescription(e.target.value)}
                  placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, and what you were trying to accomplish..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={creatingTicket}
                />
                <p className="mt-2 text-xs text-gray-500">
                  The more detail you provide, the faster we can help resolve your issue.
                </p>
              </div>

              {/* Submitter Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Submitting as:</span> {currentUser?.firstname} {currentUser?.lastname} ({currentUser?.email})
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTicketSubject('');
                  setNewTicketDescription('');
                  setNewTicketPriority('medium');
                }}
                disabled={creatingTicket}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={!newTicketSubject.trim() || !newTicketDescription.trim() || creatingTicket}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creatingTicket ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Ticket
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Tickets;
